import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useState, useCallback } from 'react';
import { DropzoneField } from 'components/DropzoneField/DropzoneField';
import { NotFound } from 'components/NotFound/NotFound';
import thriftyLogo from 'assets/images/thrifty-logo.svg';
import { useCustomerDocumentUpload } from 'hooks/useCustomerDocumentUpload';

interface FileState {
  dLFront: File | null;
  dLBack: File | null;
  passportFront: File | null;
  passportBack: File | null;
}

const initialFileState: FileState = {
  dLFront: null,
  dLBack: null,
  passportFront: null,
  passportBack: null,
};

const documentFields: { key: keyof FileState; title: string }[] = [
  { key: 'dLFront', title: "Driver's License Front" },
  { key: 'dLBack', title: "Driver's License Back" },
  { key: 'passportFront', title: 'Passport/Emirates ID/GCC ID Front' },
  { key: 'passportBack', title: 'Passport/Emirates ID/GCC ID Back' },
];

export const DocumentUpload = () => {
  const { id } = useParams<{ id: string }>();

  const [files, setFiles] = useState<FileState>(initialFileState);

  const { handleUpload, isUploading, uploadError, validationError } =
    useCustomerDocumentUpload();

  if (!id) return <NotFound />;

  const handleFileChange = useCallback(
    (key: keyof FileState, file: File | null) => {
      setFiles((prev) => ({
        ...prev,
        [key]: file,
      }));
    },
    []
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append('id', id);

    Object.entries(files).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    try {
      // await handleUpload(formData);
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '90vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2,
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 700,
          borderRadius: 4,
          boxShadow: 6,
        }}
      >
        <Box sx={{ pt: 4, display: 'flex', justifyContent: 'center' }}>
          <img src={thriftyLogo} alt="Thrifty Logo" width={120} />
        </Box>

        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            textAlign="center"
          >
            Upload Your Documents
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={3}>
              {documentFields.map(({ key, title }) => (
                <Grid item xs={12} sm={6} key={key}>
                  <DropzoneField
                    label={title}
                    value={files[key]}
                    onDrop={(file) => handleFileChange(key, file)}
                  />
                </Grid>
              ))}

              {(uploadError || validationError) && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">
                    {uploadError || validationError}
                  </Typography>
                </Grid>
              )}

              <Grid
                item
                xs={12}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={isUploading}
                  sx={{ mt: 3, mb: 2, borderRadius: 2 }}
                >
                  {isUploading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Upload Documents'
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};
